
import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';

import { FleetChecklistUpdate, FleetChecklist, FleetChecklistSummary, FleetChecklistCreate, FleetStatus } from '../models/fleet-checklist';
import { VehicleValidation, EvidenceFile, VehicleValidationStatus, EquipmentStatus } from '../models/vehicle-validation';
import { ProgramacionDataResponse, VehicleConvoy } from '../models/vehicle.convoy';
import { DynamicFiledSelected } from '../models/dynamic-field';
import { ValidationCriterion, CriterionSelected } from '../models/validation-criterion';


@Injectable({
  providedIn: 'root'
})
export class FleetChecklistApiService {

  constructor(
    private http: HttpClient
  ) { }

  async getFleetsSummary(): Promise<FleetChecklistSummary[]> {
    let url = `${environment.apiUrl}/api/checklist/groups`;

    try {
      let res = await this.http.get<any>(url).toPromise();
      let fleets: FleetChecklistSummary[] = res.data.map((item: any) => ({
        id: item.id,
        name: item.name,
        status: item.status,
        createdAt: new Date(item.createdAt),
        updatedAt: item.updatedAt ? new Date(item.updatedAt) : undefined,
        sendetAt: item.sendetAt ? new Date(item.sendetAt) : undefined,
        type: item.type,
        vehiclesIds: item.vehiclesIds || [],
        fields: item.fields || [],
        tags: item.tags || [],
        parameters: item.parameters || {},
      }));
      return fleets
    } catch (error) {
      return []
    }
  }

  async getFleetChecklistById(id: string): Promise<FleetChecklist | null> {
    let urlDetails = `${environment.apiUrl}/api/checklist/groups/detail`;
    // return FleetChecklistHelper.getFleetChecklistMock();
    try {
      let fleetDetails = await this.http.get<any>(`${urlDetails}?group_id=${id}`).toPromise()

      if (!fleetDetails?.data) { return null }
      let details = fleetDetails.data;

      let vehicles: VehicleValidation[] = (details.vehicles || [])
        .map((vehicle: any) => {

          let fields: DynamicFiledSelected[] = vehicle.fields || []
          let criteria: CriterionSelected[] = vehicle.criteria || []

          const fieldsObject: Record<string, DynamicFiledSelected> = {};
          fields.forEach(field => {
            fieldsObject[field.id] = field;
          });


          const criteriaObject: Record<string, CriterionSelected> = {};

          criteria.forEach(criterion => {
            criteriaObject[criterion.id] = criterion;
          });

          return {
            id: vehicle.vehicle_id,
            status: vehicle.status || VehicleValidationStatus.incompleted,
            checklist_id: vehicle.id,
            unitCode: vehicle.unitCode,
            umaCode: vehicle.var_codigo_uma,
            plate: vehicle.plate,
            fields:  fieldsObject,
            criteria: criteriaObject,
            observations: vehicle.observations || '',
            parameters: vehicle.parameters || {},
          }
        })
        .sort((a: VehicleValidation, b: VehicleValidation) => a.unitCode.localeCompare(b.unitCode));

      const stateOrder: Record<string, number> = {
        OK: 1,
        OBSERVACION: 2,
        REVISIÓN: 3,
        REVISION: 3
      };

      const criteria = (details.criteria || []).map(
        (criterion: ValidationCriterion) => ({
          ...criterion,
          states: [...criterion.states].sort(
            (a, b) => (stateOrder[a.name] ?? 999) - (stateOrder[b.name] ?? 999)
          )
        })
      );

      const fleetChecklist: FleetChecklist = {
        id: details.id,
        name: details.name,
        status: details.status,
        createdAt: new Date(details.createdAt),
        updatedAt: details.updatedAt ? new Date(details.updatedAt) : undefined,
        sendetAt: details.sendetAt ? new Date(details.sendetAt) : undefined,
        deletedAt: details.deletedAt ? new Date(details.deletedAt) : undefined,
        type: details.type,
        tags: details.tags || [],
        vehicles,
        criteria: criteria,
        fields: details.fileds || [],
        parameters: details.parameters || {}
      };

      console.log('Fleet By ID from API:', fleetChecklist);

      return fleetChecklist;
    } catch (error) {
      console.error('Error fetching fleet by ID from API:', error);
      return null
    }
  }

  async createFleetChecklist(fleet: FleetChecklistCreate): Promise<FleetChecklist | null> {
    let url = `${environment.apiUrl}/api/checklist/create-group`;
    try {
      let payload = {
        name: fleet.name,
        status: fleet.status,
        type: fleet.type,
        vehiclesIds: fleet.vehiclesIds.map(id => Number(id)),
        tags: fleet.tags || [],
        fields: fleet.fields || [],
        parameters: fleet.parameters || {},
      };
      let res = await this.http.post<any>(url, payload).toPromise();
      let create = res.data
      console.log('Fleets from API:', create);

      let vehicles: VehicleValidation[] = (create.checklists || []).map((vehicle: any) => {
        let fields: DynamicFiledSelected[] = vehicle.fields || []
        let criteria: CriterionSelected[] = vehicle.criteria || []
        const fieldsObject: Record<string, DynamicFiledSelected> = {};
        fields.forEach(field => {
          fieldsObject[field.id] = field;
        });
        const criteriaObject: Record<string, CriterionSelected> = {};
        criteria.forEach(criterion => {
          criteriaObject[criterion.id] = criterion;
        });

        return {
          id: vehicle.vehicle_id,
          checklist_id: vehicle.id || '',
          unitCode: vehicle.unitCode || '',
          plate: vehicle.plate || '',
          fields: fieldsObject,
          criteria: criteriaObject,
          observations: vehicle.observations || '',
          parameters: vehicle.parameters || {},
          status: vehicle.status || VehicleValidationStatus.incompleted,
        }
      })
        .sort((a: VehicleValidation, b: VehicleValidation) => a.unitCode.localeCompare(b.unitCode));


      let newFleet: FleetChecklist = {
        id: create.id,
        name: create.name,
        status: create.status,
        createdAt: new Date(create.createdAt),
        updatedAt: new Date(create.updatedAt),
        type: create.type,
        fields: create.fields || [],
        tags: create.tags || [],
        criteria: create.criteria || [],
        vehicles,
        parameters: create.parameters || {}
      }
      return newFleet
    } catch (error) {
      console.error('Error fetching fleets from API:', error);
      return null
    }
  }

  async finishFleetChecklist(fleetId: string,): Promise<boolean | null> {
    let url = `${environment.apiUrl}/api/checklist/groups`;
    try {
      let payload = {
        group_id: fleetId,
        status: FleetStatus.COMPLETED,
      };
      let res = await this.http.put<any>(url, payload).toPromise();
      if (res.success) return true
      return false
    } catch (error) {
      return false
    }
  }

   async updateFleetChecklistStatus(fleetId: string, status: FleetStatus): Promise<boolean | null> {
    let url = `${environment.apiUrl}/api/checklist/groups`;
    try {
      let payload = {
        group_id: fleetId,
        status: status,
      };
      let res = await this.http.put<any>(url, payload).toPromise();
      if (res.success) return true
      return false
    } catch (error) {
      return false
    }
  }

  async updateFleetChecklist(fleet: FleetChecklistUpdate): Promise<boolean | null> {
    let url = `${environment.apiUrl}/api/checklist/groups`;
    try {
      let payload: any = {
        group_id: fleet.id,
        ...(fleet.name ? { name: fleet.name } : {}),
        ...(fleet.status ? { status: fleet.status } : {}),
        ...(fleet.type ? { type: fleet.type } : {}),
        ...(fleet.tags ? { tags: fleet.tags } : {}),
        ...(fleet.vehicles?.length
          ? { vehiclesIds: fleet.vehicles.map(v => Number(v.id)) }
          : {}),
        ...(fleet.vehiclesIds?.length ? { vehiclesIds: fleet.vehiclesIds } : {}),
        ...(fleet.parameters ? { parameters: fleet.parameters } : {})
      };
      let res = await this.http.put<any>(url, payload).toPromise();

      if (res.success) {
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }
  async updateFleetVehiclesValidation(fleetId: string, modifiedVehicles: VehicleValidation[]): Promise<boolean | null> {
    let url = `${environment.apiUrl}/api/checklist/groups/bulk-update`;
    try {
      let checklists = modifiedVehicles.map(v => {
        return {
          checklist_id: v.checklist_id,
          status: v.status || 'pending',
          items: Object.values(v.criteria).map(c => {
            return {
              criterio_id: Number(c.id),
              state_id: Number(c.selectedStateId)
            }
          }),
          observations: v.observations,
          fields : Object.values(v.fields),
          parameters: v.parameters || {}
        }
      })
      let payload = {
        group_id: fleetId,
        checklists,
      };
      let res = await this.http.post<any>(url, payload).toPromise();
      if (res && res.success) { return true }
      return false
    } catch (error) {
      return null
    }
  }

  async deleteVehicleValidation(id: string): Promise<boolean | null> {
    let url = `${environment.apiUrl}/api/checklist/vehicle-checklist/${id}`;

    try {
      let res = await this.http.delete<any>(url).toPromise();
      if (!res.success) {
        return false
      }
      return true
    } catch (error) {
      return false
    }
  }
  async deleteFleetChecklist(id: string): Promise<boolean | null> {
    let url = `${environment.apiUrl}/api/checklist/groups/${id}`;

    try {
      let res = await this.http.delete<any>(url).toPromise();
      if (!res.success) {
        return false
      }
      return true
    } catch (error) {
      return false
    }
  }

  async uploadFleetEvidence(fleetId: string, evidence: EvidenceFile): Promise<string | null> {
    let urlCritera = `${environment.apiUrl}/api/checklist/upload-evidence`;
    try {
      let formData = new FormData();
      formData.append('checklist_id', fleetId);
      formData.append('file', evidence.file);

      let res = await this.http.post<any>(urlCritera, formData).toPromise();

      if (res.success) {
        return res.data.uploaded[0].url || null
      }
      return null
    } catch (error) {
      return null;
    }
  }

  async removeFleetEvidence(fleetId: string, path: string): Promise<boolean | null> {
    let url = `${environment.apiUrl}/api/checklist/remove-evidence`;
    try {
      let payload = {
        checklist_id: fleetId,
        path
      };
      const res = await this.http.request<any>(
        'DELETE',
        url,
        {
          body: {
            checklist_id: fleetId,
            path
          }
        }
      ).toPromise();

      return !!res?.success;
    } catch (error) {
      return false
    }
  }

  // utils
  async extractConvoystFromImage(file: File): Promise<ProgramacionDataResponse | null> {
    let url = `${environment.robotUrl}/upload/external-shedule`;
    try {
      let formData = new FormData();
      formData.append('file', file);
      let res = await this.http.post<any>(url, formData).toPromise();
      if (res.success) {
        return res.data || null
      }
      return null
    } catch (error) {
      return null
    }
  }
  async sendFleetChecklistEmail(fleetId: string, withPDF: boolean = false): Promise<boolean> {
    const url = `${environment.robotUrl}/send/fleet-checklist?id=${fleetId}&pdf=${withPDF}`;
    try {
      const res = await this.http.get<any>(url).toPromise();
      return !!res?.success;
    } catch (error) {
      console.error(error);
      return false;
    }
  }
  async generateReportPDF(fleetId: string) {
    const url = `${environment.robotUrl}/report/validation/pdf?id=${fleetId}`;

    try {
      return await this.http.get(url, {
        responseType: 'blob',
        observe: 'response'
      }).toPromise();
    } catch (error) {
      console.error(error);
      return null;
    }
  }
  async getLastEventOperative(vehicles : any[], from : Date, to : Date) : Promise<EquipmentStatus[] | null> {

    let url = `${environment.apiUrl}/api/reports/ultimos_eventos_registrados`
    const getLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    try {
      let vehiclesJSON =JSON.stringify(vehicles)
      let payload = {
        vehiculos : vehiclesJSON,
        fecha_desde: `${getLocalDate(from)}T00:00:00-05:00`,
        fecha_hasta: `${getLocalDate(to)}T23:59:00-05:00`,
      }

      let res = await this.http.post<any>(url, payload).toPromise();

      if(!res || !res[0] || !res[0][1]) return null
      let operatives = res[0][1]


      // estos valore representan la fecha y el nombre del ultimo evento (- significa vacio que no hubo evento)
      // la fecha llega por ejemplo 2026\/06\/18 13:53:33
      // last_adas_tipo_evento
      // last_adas_fecha
      // last_alcohol_fecha
      // last_alcohol_tipo_evento

      let equipments : EquipmentStatus[] = operatives.map((o : any) => {
        const parseDate = (d: any) => {
          if (!d || d === '-') return undefined;
          const parsed = new Date(d);
          return isNaN(parsed.getTime()) ? undefined : parsed;
        };
        const parseEventName = (name: any) => {
          return name && name !== '-' ? name : undefined;
        };
        return {
          plate : o.placa,
          alcohol : parseDate(o.last_alcohol_fecha),
          adas : parseDate(o.last_adas_fecha),
          alcoholEventName: parseEventName(o.last_alcohol_tipo_evento),
          adasEventName: parseEventName(o.last_adas_tipo_evento)
        }
      })
      return equipments
    } catch (error) {
      return null
    }

  }

}
